import { Button } from '@material-ui/core'
import React, { lazy, Suspense, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import {
  HashRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom'

import { isProd } from './ApiPath'
import ContactUs from './components/contactUs/ContactUs'
import Home from './components/Home'
import HomeLayout from './components/Home/HomeLayout'
import PretrainedAIModels from './components/Home/PretrainedAIModels'
import PricingTab from './components/Home/PricingTab'
import Redaction from './components/Home/redaction'
import Loading from './components/others/Loading'
import PublicRouter from './components/router/PublicRouter'
import Layout from './pages/Layout'
import ProtectRouter from './ProtectRouter'
import TaggingApi from './services/TaggingApi'

// Declare Mouseflow global variable type
declare global {
  interface Window {
    _mfq: any[]
    mouseflow: {
      start: () => void
      stop: () => void
    }
  }
}
const MyProjects = lazy(() => import('./components/MyProjects'))
const ProjectDashboard = lazy(() => import('./components/ProjectDashboard'))
const TrainingFiles = lazy(() => import('./components/TrainingFiles'))
const TrainingJobs = lazy(() => import('./components/TrainingJobs'))
const ProcessingFiles = lazy(() => import('./components/ProcessingFiles'))
const MyAccount = lazy(() => import('./components/MyAccount'))
const AiChat = lazy(() => import('./pages/AiChat'))
const KnowledgeBase = lazy(() => import('./components/KnowledgeBase'))

const SuspenseWrapper = (children: JSX.Element) => {
  return <Suspense fallback={<Loading />}>{children}</Suspense>
}

// Special component to handle route with session key
const BusinessRoute = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams<{ businessId: string }>()
  const businessId = params.businessId

  useEffect(() => {
    const handleSessionKey = async () => {
      const hashQuery = location.hash.split('?')[1] || ''
      const skey = hashQuery ? new URLSearchParams(hashQuery).get('skey') : null
      if (skey && businessId) {
        try {
          console.log(`Get session key for studio-web`)
          const taggingApi = TaggingApi.getInstance()
          await taggingApi.getCookieForStudioWebDomain(skey)
        } catch (error) {
          console.error('Error fetching session key:', error)
        }
      }
    }
    handleSessionKey()
  }, [location, navigate, businessId])

  return (
    <ProtectRouter>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Layout />
      </ErrorBoundary>
    </ProtectRouter>
  )
}

const AppRoutes = () => {
  useEffect(() => {
    const loadAiAssistantScript = () => {
      const script = document.createElement('script')
      script.type = 'module' // This is important to load the script as a module to avoid conflicts with the global scope
      script.src = isProd
        ? 'https://doc.com.ai/ai-assistant/latest/static/js/main.js'
        : 'https://uat.doc.com.ai/ai-assistant/latest/static/js/main.js'
      script.async = true
      document.body.appendChild(script)
    }
    loadAiAssistantScript()

    const loadMouseflowScript = () => {
      window._mfq = window._mfq || []
      const mf = document.createElement('script')
      mf.type = 'text/javascript'
      mf.defer = true
      mf.src =
        '//cdn.mouseflow.com/projects/ac11ab81-81d9-45ac-81e8-50d77bcb2a50.js'
      document.body.appendChild(mf)
    }
    loadMouseflowScript()

    const activateHotJar = () => {
      const hjScript = document.createElement('script')
      hjScript.async = true
      hjScript.innerHTML = `
      (function (c, s, q, u, a, r, e) {
        c.hj=c.hj||function(){(c.hj.q=c.hj.q||[]).push(arguments)};
        c._hjSettings = { hjid: a };
        r = s.getElementsByTagName('head')[0];
        e = s.createElement('script');
        e.async = true;
        e.src = q + c._hjSettings.hjid + u;
        r.appendChild(e);
    })(window, document, 'https://static.hj.contentsquare.net/c/csq-', '.js', 6397588);
    `
      document.getElementsByTagName('head')[0].appendChild(hjScript)
    }
    activateHotJar()
  }, [])

  return (
    <HashRouter>
      <Routes>
        <Route path={'business/:businessId'} element={<BusinessRoute />}>
          <Route index element={<Navigate to={'projects'} replace={true} />} />
          <Route
            path="projects"
            element={
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {SuspenseWrapper(<MyProjects />)}
              </ErrorBoundary>
            }
          />
          <Route
            path="chat"
            element={
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {SuspenseWrapper(<AiChat />)}
              </ErrorBoundary>
            }
          />
          <Route
            path="settings"
            element={
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {SuspenseWrapper(<MyAccount />)}
              </ErrorBoundary>
            }
          />
          <Route path="project/:projectId">
            <Route
              index
              element={<Navigate to={'dashboard'} replace={true} />}
            />
            <Route
              path="dashboard"
              element={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  {SuspenseWrapper(<ProjectDashboard />)}
                </ErrorBoundary>
              }
            />
            <Route
              path="label"
              element={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  {SuspenseWrapper(<TrainingFiles />)}
                </ErrorBoundary>
              }
            />
            <Route
              path="train"
              element={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  {SuspenseWrapper(<TrainingJobs />)}
                </ErrorBoundary>
              }
            />
            <Route
              path="use"
              element={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  {SuspenseWrapper(<ProcessingFiles />)}
                </ErrorBoundary>
              }
            />
            <Route
              path="knowledge"
              element={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  {SuspenseWrapper(<KnowledgeBase />)}
                </ErrorBoundary>
              }
            />
            <Route
              path="chat"
              element={
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  {SuspenseWrapper(<AiChat />)}
                </ErrorBoundary>
              }
            />
            <Route
              path="*"
              element={<Navigate to={'dashboard'} replace={true} />}
            />
          </Route>
          <Route
            path="*"
            element={<Navigate to={'projects'} replace={true} />}
          />
        </Route>
        <Route path="/" element={<PublicRouter />}>
          <Route
            index
            element={
              <HomeLayout>
                <Home />
              </HomeLayout>
            }
          />
          <Route
            path="pricing"
            element={
              <HomeLayout>
                <PricingTab />
              </HomeLayout>
            }
          />
          <Route
            path="privacy-redaction"
            element={
              <HomeLayout>
                <Redaction />
              </HomeLayout>
            }
          />
          {/* for external use */}
          <Route
            path="Pre-trained-AI-Models"
            element={<PretrainedAIModels />}
          />
          <Route path="contact-us" element={<ContactUs />} />
        </Route>
      </Routes>
    </HashRouter>
  )
}

function ErrorFallback({ error, resetErrorBoundary }: any) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
      <Button onClick={resetErrorBoundary}>Try again</Button>
    </div>
  )
}

export default AppRoutes
